import { useRef, useState } from 'react';
import { Message, Sender } from 'services/conversations/types';
import { useQuery, useSubscriber } from 'services/api/hooks';
import { getMessagesByConversation } from 'services/conversations/api';
import {
  onCreateMessage,
  onUpdateMessage
} from 'services/conversations/subscriptions';

type Props = {
  conversationId: string;
};

export const useConversationMessages = ({ conversationId }: Props) => {
  const messagesListRef = useRef<Message[]>([]);
  const [messagesList, setMessagesList] = useState<Message[]>([]);

  const { isLoading } = useQuery<{ items: Message[] }>(
    getMessagesByConversation,
    {
      conversationId
    },
    {
      onFetchSuccess: (data) => {
        messagesListRef.current = data.items;
        setMessagesList(messagesListRef.current);
      }
    }
  );

  const updateMessageInList = (message: Message) => {
    const messageIndex = messagesListRef.current.findIndex(
      (m) => m.id === message.id
    );
    if (messageIndex === -1) return;

    messagesListRef.current[messageIndex] = {
      ...messagesListRef.current[messageIndex],
      ...message
    };

    setMessagesList(messagesListRef.current);
  };

  const addMessageToList = (message: Message) => {
    messagesListRef.current = [...messagesListRef.current, message];
    setMessagesList(messagesListRef.current);
  };

  useSubscriber({
    subQuery: onUpdateMessage,
    subParams: {
      conversationId: { eq: conversationId }
    },
    onFetchSuccess: (data) => {
      const updatedMessage: Message = data?.onUpdateMessage;
      updateMessageInList(updatedMessage);
    }
  });

  useSubscriber({
    subQuery: onCreateMessage,
    subParams: {
      conversationId: { eq: conversationId || '' },
      or: [
        // to not interfere with Sender.USER that is created from the UI
        { sender: { eq: Sender.AGENT } },
        { sender: { eq: Sender.PROSPECT } }
      ]
    },
    onFetchSuccess: (data) => {
      const message: Message = data?.onCreateMessage;
      addMessageToList(message);
    }
  });

  return {
    isLoading,
    messagesList,
    addMessageToList
  };
};
